// `??` (not `||`) so an empty VITE_API_ENDPOINT (same-origin proxy) stays "".
const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

/** Mirrors panel-api `MCP_SCOPE_READ`. Dashboard must not import the API package. */
export const MCP_READ_SCOPE = 'mcp:read';
export const MCP_WRITE_SCOPE = 'mcp:write';

export function apiUrl(path: string): string {
  const base = String(API_BASE_URL).replace(/\/$/, '');
  return `${base}${path}`;
}

/** Scopes shown on the consent page; always includes `mcp:read`. */
export function requestedMcpScopes(searchParams: URLSearchParams): string[] {
  const scopes = (searchParams.get('scope') ?? '')
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (!scopes.includes(MCP_READ_SCOPE)) {
    scopes.push(MCP_READ_SCOPE);
  }
  return scopes;
}

/**
 * First-hop authorize bounce from `/login`. Requires the OAuth request params
 * BetterAuth stored on the unauthenticated authorize redirect — not merely
 * `client_id` (the consent page also has that).
 */
export function isMcpOAuthAuthorizeReturn(searchParams: URLSearchParams): boolean {
  return Boolean(
    searchParams.get('client_id') &&
      (searchParams.get('redirect_uri') || searchParams.get('code_challenge')),
  );
}

/**
 * Parameters BetterAuth adds when it signs the authorize request it sends to
 * the login and consent pages. They describe that hop, not the request, so a
 * fresh authorize call drops them and the provider signs anew.
 */
const SIGNED_QUERY_PARAMS = ['sig', 'exp', 'ba_iat', 'ba_param', 'ba_pl'];

export function mcpAuthorizeUrl(searchParams: URLSearchParams): string {
  const params = new URLSearchParams(searchParams);
  params.delete('returnTo');
  for (const name of SIGNED_QUERY_PARAMS) {
    params.delete(name);
  }
  const prompt = params.get('prompt') ?? '';
  if (!prompt.split(/\s+/).includes('consent')) {
    params.set('prompt', [prompt, 'consent'].filter(Boolean).join(' ').trim());
  }
  const scopes = requestedMcpScopes(params);
  if (params.get('scope') !== scopes.join(' ')) {
    params.set('scope', scopes.join(' '));
  }
  return apiUrl(`/api/auth/oauth2/authorize?${params.toString()}`);
}

export function mcpConsentUrl(): string {
  return apiUrl('/api/auth/oauth2/consent');
}

/**
 * The signed authorize request the consent page was opened with, as the
 * consent endpoint expects it in `oauth_query`: `sig`, and only the
 * parameters `ba_param` lists as signed. Anything appended after signing
 * would fail verification. Null when the page was not opened by a signed
 * redirect (an expired or hand-typed link).
 *
 * Mirrors `buildSignedOAuthQuery` in `@better-auth/oauth-provider/client`,
 * which the dashboard does not depend on.
 */
export function signedOAuthQuery(searchParams: URLSearchParams): string | null {
  const signedNames = new Set(searchParams.getAll('ba_param'));
  if (!searchParams.has('sig') || signedNames.size === 0) {
    return null;
  }
  const signed = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (key === 'sig' || key === 'ba_param' || signedNames.has(key)) {
      signed.append(key, value);
    }
  }
  return signed.toString();
}
